import axiosInstance from "@/lib/axios";
import type {
  Tenant,
  TenantContact,
  TenantConfig,
  TenantStats,
  ServiceCatalog,
  TenantServiceEntry,
  TenantCredentialsResponse,
  TenantCredentialUpdate,
  DeploymentJob,
  TenantUser,
  TenantUserCreate,
  LoadTestOverview,
  LoadTestRunRequest,
  BookingLoadTestRun,
} from "@/types";

export const tenantService = {
  list: async (params?: {
    search?: string;
    status?: string;
  }): Promise<Tenant[]> => {
    const response = await axiosInstance.get<Tenant[]>("tenants/", { params });
    // DRF may return paginated or flat list
    const data = response.data;
    return Array.isArray(data)
      ? data
      : (data as unknown as { results: Tenant[] }).results;
  },

  get: async (id: string): Promise<Tenant> => {
    const response = await axiosInstance.get<Tenant>(`tenants/${id}/`);
    return response.data;
  },

  create: async (data: Partial<Tenant>): Promise<Tenant> => {
    const response = await axiosInstance.post<Tenant>("tenants/", data);
    return response.data;
  },

  update: async (id: string, data: Partial<Tenant>): Promise<Tenant> => {
    const response = await axiosInstance.patch<Tenant>(`tenants/${id}/`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await axiosInstance.delete(`tenants/${id}/`);
  },

  stats: async (): Promise<TenantStats> => {
    const response = await axiosInstance.get<TenantStats>("tenants/stats/");
    return response.data;
  },

  // Config
  getConfig: async (tenantId: string): Promise<TenantConfig> => {
    const response = await axiosInstance.get<TenantConfig>(
      `tenants/${tenantId}/config/`,
    );
    return response.data;
  },

  updateConfig: async (
    tenantId: string,
    data: Partial<TenantConfig>,
  ): Promise<TenantConfig> => {
    const response = await axiosInstance.patch<TenantConfig>(
      `tenants/${tenantId}/config/`,
      data,
    );
    return response.data;
  },

  // Contacts
  listContacts: async (tenantId: string): Promise<TenantContact[]> => {
    const response = await axiosInstance.get<TenantContact[]>(
      `tenants/${tenantId}/contacts/`,
    );
    return response.data;
  },

  createContact: async (
    tenantId: string,
    data: Partial<TenantContact>,
  ): Promise<TenantContact> => {
    const response = await axiosInstance.post<TenantContact>(
      `tenants/${tenantId}/contacts/`,
      data,
    );
    return response.data;
  },

  updateContact: async (
    tenantId: string,
    contactId: string,
    data: Partial<TenantContact>,
  ): Promise<TenantContact> => {
    const response = await axiosInstance.patch<TenantContact>(
      `tenants/${tenantId}/contacts/${contactId}/`,
      data,
    );
    return response.data;
  },

  deleteContact: async (tenantId: string, contactId: string): Promise<void> => {
    await axiosInstance.delete(`tenants/${tenantId}/contacts/${contactId}/`);
  },

  // Service Catalog
  getServiceCatalog: async (): Promise<ServiceCatalog[]> => {
    const response = await axiosInstance.get<ServiceCatalog[]>(
      "tenants/services/catalog/",
    );
    return response.data;
  },

  // Tenant Services
  getTenantServices: async (
    tenantId: string,
  ): Promise<TenantServiceEntry[]> => {
    const response = await axiosInstance.get<TenantServiceEntry[]>(
      `tenants/${tenantId}/services/`,
    );
    return response.data;
  },

  updateTenantServices: async (
    tenantId: string,
    services: Array<{ service_id: string; enabled: boolean }>,
  ): Promise<TenantServiceEntry[]> => {
    const response = await axiosInstance.put<TenantServiceEntry[]>(
      `tenants/${tenantId}/services/`,
      { services },
    );
    return response.data;
  },

  // Credentials (per-tenant API keys / secrets)
  getCredentials: async (
    tenantId: string,
  ): Promise<TenantCredentialsResponse> => {
    const response = await axiosInstance.get<TenantCredentialsResponse>(
      `tenants/${tenantId}/credentials/`,
    );
    return response.data;
  },

  updateCredentials: async (
    tenantId: string,
    credentials: TenantCredentialUpdate[],
  ): Promise<TenantCredentialsResponse> => {
    const response = await axiosInstance.put<TenantCredentialsResponse>(
      `tenants/${tenantId}/credentials/`,
      { credentials },
    );
    return response.data;
  },

  // Deployment
  getDeployReadiness: async (
    tenantId: string,
  ): Promise<{ ready: boolean; issues: string[] }> => {
    const response = await axiosInstance.get<{
      ready: boolean;
      issues: string[];
    }>(`tenants/${tenantId}/deploy-readiness/`);
    return response.data;
  },

  deployTenant: async (tenantId: string): Promise<DeploymentJob> => {
    const response = await axiosInstance.post<DeploymentJob>(
      `tenants/${tenantId}/deploy/`,
    );
    return response.data;
  },

  // Tenant Users (Central Auth)
  listUsers: async (tenantId: string): Promise<TenantUser[]> => {
    const response = await axiosInstance.get<TenantUser[]>(
      `tenants/${tenantId}/users/`,
    );
    return response.data;
  },

  createUser: async (
    tenantId: string,
    data: TenantUserCreate,
  ): Promise<TenantUser> => {
    const response = await axiosInstance.post<TenantUser>(
      `tenants/${tenantId}/users/`,
      data,
    );
    return response.data;
  },

  updateUser: async (
    tenantId: string,
    userId: string,
    data: Partial<TenantUser> & { password?: string },
  ): Promise<TenantUser> => {
    const response = await axiosInstance.patch<TenantUser>(
      `tenants/${tenantId}/users/${userId}/`,
      data,
    );
    return response.data;
  },

  deleteUser: async (tenantId: string, userId: string): Promise<void> => {
    await axiosInstance.delete(`tenants/${tenantId}/users/${userId}/`);
  },

  // Booking load tester
  getLoadTest: async (tenantId: string): Promise<LoadTestOverview> => {
    const response = await axiosInstance.get<LoadTestOverview>(
      `tenants/${tenantId}/load-test/run/`,
    );
    return response.data;
  },

  runLoadTest: async (
    tenantId: string,
    params: LoadTestRunRequest,
  ): Promise<BookingLoadTestRun> => {
    const response = await axiosInstance.post<BookingLoadTestRun>(
      `tenants/${tenantId}/load-test/run/`,
      params,
    );
    return response.data;
  },

  purgeLoadTest: async (tenantId: string): Promise<{ deleted: number }> => {
    const response = await axiosInstance.post<{ deleted: number }>(
      `tenants/${tenantId}/load-test/purge/`,
    );
    return response.data;
  },
};
